import { Box, Typography } from "@mui/material";
import TimeIcon from "@mui/icons-material/AccessTime";
import useCountdown from "../../hooks/useCountdown";
import useCountdownFromServer from "../../hooks/useCountdownFromServer";
import ThemeStore from "../../stores/ThemeStore";
import { observer } from "mobx-react-lite";

type Props = {
    time?: number;
    expiresInSeconds: number;
    onTimeUp: () => void;
    prefix?: string;
    suffix?: string;
};

const CartExpiryBanner = observer(
    ({
        time,
        expiresInSeconds,
        onTimeUp,
        prefix = "You have",
        suffix = "to checkout.",
    }: Props) => {
        // Use server-based countdown if available, otherwise fall back to client-based
        const serverTimeRemaining = useCountdownFromServer(
            expiresInSeconds,
            onTimeUp,
        );

        const timeRemaining = serverTimeRemaining;

        // Use theme primary color or fallback to orange gradient
        const primaryColor = ThemeStore.primaryColor || "#FF6B35";
        const isDefaultColor =
            !ThemeStore.primaryColor || ThemeStore.primaryColor === "#a76a10";

        // Create gradient colors based on primary color or use orange as fallback
        const gradientStart = isDefaultColor ? "#FF6B35" : primaryColor;
        const gradientEnd = isDefaultColor ? "#F7931E" : `${primaryColor}CC`; // Add some transparency for variation

        return (
            <Box
                sx={{
                    background: `linear-gradient(135deg, ${gradientStart} 0%, ${gradientEnd} 100%)`,
                    borderRadius: "12px",
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    boxShadow: isDefaultColor
                        ? "0 3px 15px rgba(255, 107, 53, 0.3)"
                        : `0 3px 15px ${primaryColor}33`,
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    position: "relative",
                    overflow: "hidden",
                    animation: "pulse 2s infinite",
                    "@keyframes pulse": {
                        "0%": {
                            boxShadow: isDefaultColor
                                ? "0 3px 15px rgba(255, 107, 53, 0.3)"
                                : `0 3px 15px ${primaryColor}33`,
                        },
                        "50%": {
                            boxShadow: isDefaultColor
                                ? "0 5px 25px rgba(255, 107, 53, 0.5)"
                                : `0 5px 25px ${primaryColor}55`,
                        },
                        "100%": {
                            boxShadow: isDefaultColor
                                ? "0 3px 15px rgba(255, 107, 53, 0.3)"
                                : `0 3px 15px ${primaryColor}33`,
                        },
                    },
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: "-100%",
                        width: "100%",
                        height: "100%",
                        background:
                            "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)",
                        animation: "shimmer 3s infinite",
                    },
                    "@keyframes shimmer": {
                        "0%": {
                            left: "-100%",
                        },
                        "100%": {
                            left: "100%",
                        },
                    },
                }}
            >
                <TimeIcon
                    sx={{
                        color: "white",
                        fontSize: 24,
                        filter: "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3))",
                    }}
                />
                <Typography
                    variant="body2"
                    sx={{
                        color: "white",
                        fontWeight: "600",
                        textShadow: "0 1px 2px rgba(0, 0, 0, 0.3)",
                        flex: 1,
                    }}
                >
                    {prefix}{" "}
                    <Typography
                        component="span"
                        sx={{
                            fontWeight: "bold",
                            fontSize: "1.1em",
                            textDecoration: "underline",
                            textDecorationColor: "rgba(255, 255, 255, 0.7)",
                        }}
                    >
                        {timeRemaining}
                    </Typography>{" "}
                    {suffix}
                </Typography>
            </Box>
        );
    },
);

export default CartExpiryBanner;
